/**
 * AutoCursor — cursor that auto-measures target element positions by ID.
 * Place inside ScreenMock as a sibling of the mock content.
 * Use `target: "element-id"` to point at elements, or `x/y` for fixed positions.
 *
 * Uses offsetWidth/getBoundingClientRect ratio to compensate for Remotion's
 * CSS transform scaling, so positions are correct at any Player size.
 */

import { useCurrentFrame, interpolate, Easing } from "remotion";
import { useLayoutEffect, useRef, useReducer } from "react";

interface AutoCursorPoint {
  /** Element ID to target (measures center). */
  target?: string;
  /** Explicit x coordinate (for rest/idle positions). */
  x?: number;
  /** Explicit y coordinate (for rest/idle positions). */
  y?: number;
  /** Frame number for this keyframe. */
  frame: number;
  /** Show click animation at this point. */
  click?: boolean;
}

export function AutoCursor({ points, color = "#fff" }: { points: AutoCursorPoint[]; color?: string }) {
  const frame = useCurrentFrame();
  const cursorRef = useRef<HTMLDivElement>(null);
  const measured = useRef<Record<string, { x: number; y: number }>>({});
  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  const didInitialMeasure = useRef(false);

  useLayoutEffect(() => {
    // Find the cursor's positioning container (ScreenMock content div, which has position: relative)
    const container = cursorRef.current?.offsetParent as HTMLElement | null;
    if (!container) return;

    const cr = container.getBoundingClientRect();
    // Scale factor: CSS layout size vs visual (possibly transformed) size
    const scaleX = cr.width > 0 ? container.offsetWidth / cr.width : 1;
    const scaleY = cr.height > 0 ? container.offsetHeight / cr.height : 1;

    for (const pt of points) {
      if (!pt.target) continue;
      const el = document.getElementById(pt.target);
      if (!el) continue;

      const er = el.getBoundingClientRect();
      // Position in viewport space, then convert to CSS layout space via scale factor
      measured.current[pt.target] = {
        x: Math.round((er.left - cr.left + er.width / 2) * scaleX),
        y: Math.round((er.top - cr.top + er.height / 2) * scaleY),
      };
    }

    // Force one re-render after initial measurement so cursor has correct positions
    if (!didInitialMeasure.current) {
      didInitialMeasure.current = true;
      forceRender();
    }
  });

  function resolve(pt: AutoCursorPoint): { x: number; y: number } {
    if (pt.x !== undefined && pt.y !== undefined) return { x: pt.x, y: pt.y };
    if (pt.target && measured.current[pt.target]) return measured.current[pt.target];
    // Fallback: use the first explicit x/y point as default instead of (0,0)
    for (const p of points) {
      if (p.x !== undefined && p.y !== undefined) return { x: p.x, y: p.y };
    }
    return { x: 0, y: 0 };
  }

  const resolved = points.map(resolve);
  let x = resolved[0]?.x ?? 0;
  let y = resolved[0]?.y ?? 0;
  let clicking = false;

  for (let i = 0; i < resolved.length - 1; i++) {
    const from = resolved[i];
    const to = resolved[i + 1];
    const ff = points[i].frame;
    const tf = points[i + 1].frame;
    if (frame >= ff && frame <= tf) {
      const p = interpolate(frame, [ff, tf], [0, 1], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
        easing: Easing.inOut(Easing.cubic),
      });
      x = interpolate(p, [0, 1], [from.x, to.x]);
      y = interpolate(p, [0, 1], [from.y, to.y]);
      break;
    }
    if (frame > tf) {
      x = to.x;
      y = to.y;
    }
  }

  for (const pt of points) {
    if (pt.click && Math.abs(frame - pt.frame) < 4) clicking = true;
  }

  const cs = clicking ? interpolate(frame % 8, [0, 3, 6], [1, 0.85, 1], { extrapolateRight: "clamp" }) : 1;
  const cr = clicking ? interpolate(frame % 12, [0, 6, 12], [0, 1, 0], { extrapolateRight: "clamp" }) : 0;

  return (
    <div ref={cursorRef} style={{ position: "absolute", left: x, top: y, transform: `scale(${cs})`, pointerEvents: "none", zIndex: 9999 }}>
      {clicking && (
        <div style={{ position: "absolute", width: 40, height: 40, borderRadius: "50%", border: `2px solid ${color}`, opacity: 1 - cr, transform: `translate(-50%, -50%) scale(${0.5 + cr * 1.5})` }} />
      )}
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))" }}>
        <path d="M5 3L19 12L12 13L9 20L5 3Z" fill={color} stroke="#000" strokeWidth="1" />
      </svg>
    </div>
  );
}
